var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import rehypeRaw from 'rehype-raw';
import { List, ListItem } from '../List';
import { Paragraph } from '../Paragraph';
import { HEADING_SIZE, Heading } from '../Heading';
import { Bold, Italic } from '../Inline';
import { firstChildAltSelector } from '../../../utils/misc';
import remarkGfm from 'remark-gfm';
import { BlockQuote } from '../BlockQuote';
import { Caption } from '../Caption';
import { sanitizeHtml } from '../../../utils/sanitizeHtml';
const parseBlockquotes = (content) => {
    const contentArray = content.split('\n');
    const newContent = [];
    let isAddingBlockquote = false;
    for (const line of contentArray) {
        if (line.startsWith('> ') && !isAddingBlockquote) {
            const newLine = line.replace('> ', '');
            newContent.push(`<blockquote>${newLine}`);
            isAddingBlockquote = true;
        }
        else if (line.startsWith('> ') && isAddingBlockquote) {
            newContent.push(line.replace('> ', ''));
        }
        else if (isAddingBlockquote) {
            newContent.push(`</blockquote>\n\n${line}`);
            isAddingBlockquote = false;
        }
        else {
            newContent.push(line);
        }
    }
    if (isAddingBlockquote) {
        newContent.push('</blockquote>');
    }
    let newString = newContent.join('\n');
    const blockquoteRegex = /<blockquote>(.*?)<\/blockquote>/g;
    const blockquotes = newString.match(blockquoteRegex);
    blockquotes === null || blockquotes === void 0 ? void 0 : blockquotes.forEach((blockquote) => {
        const linkRegex = /\[(.*?)\]\((.*?)\)/g;
        const links = blockquote.match(linkRegex);
        let newBlockquote = blockquote;
        links === null || links === void 0 ? void 0 : links.forEach((link) => {
            const newLink = link.replace(/\[(.*?)\]\((.*?)\)/, '<a href="$2">$1</a>');
            newBlockquote = newBlockquote.replace(link, newLink);
        });
        newString = newString.replace(blockquote, newBlockquote);
    });
    return newString;
};
const youtubeRegex = /^(?:https?:\/\/)?(?:www\.)?youtube\.com/;
const pullOutVideos = (content) => {
    const lines = content.split('\n');
    const mapped = lines.map((line) => {
        const lineText = line.trim();
        if (youtubeRegex.test(lineText)) {
            return `<iframe src="${lineText}" width="560" height="315" style="margin-top:24px;"></iframe>`;
        }
        else {
            return line;
        }
    });
    return mapped.join('\n');
};
const dynamicTopMarginStyles = {
    [firstChildAltSelector]: {
        mt: 0,
    },
};
export const Markdown = (_a) => {
    var { content, enableHtml, imageStyles, customStyles = {}, headingLevelOffset = 1 } = _a, rest = __rest(_a, ["content", "enableHtml", "imageStyles", "customStyles", "headingLevelOffset"]);
    let sanitizedContent = content;
    if (enableHtml) {
        sanitizedContent = parseBlockquotes(content);
        sanitizedContent = pullOutVideos(sanitizedContent);
        sanitizedContent = sanitizeHtml(sanitizedContent);
    }
    const slugify = (text) => {
        return (text
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '-')
            .replace(/[^\w\-]+/g, '')
            .replace(/--+/g, '-')
            .replace(/^-+/, '')
            .replace(/-+$/, ''));
    };
    return (_jsx("div", Object.assign({}, rest, { children: _jsx(ReactMarkdown, Object.assign({ rehypePlugins: enableHtml ? [rehypeRaw, remarkGfm] : [remarkGfm], components: {
                h1: (props) => (_jsx(Heading, Object.assign({ size: HEADING_SIZE.XL }, props, { level: props.level + headingLevelOffset, standalone: true, id: slugify(props.children.toString()), sx: Object.assign(Object.assign({}, dynamicTopMarginStyles), customStyles.h1) }))),
                h2: (props) => (_jsx(Heading, Object.assign({ size: HEADING_SIZE.LG }, props, { level: props.level + headingLevelOffset, standalone: true, id: slugify(props.children.toString()), sx: Object.assign(Object.assign({}, dynamicTopMarginStyles), customStyles.h2) }))),
                h3: (props) => (_jsx(Heading, Object.assign({ size: HEADING_SIZE.MD }, props, { level: props.level + headingLevelOffset, standalone: true, id: slugify(props.children.toString()), sx: Object.assign(Object.assign({}, dynamicTopMarginStyles), customStyles.h3) }))),
                h4: (props) => (_jsx(Heading, Object.assign({ size: HEADING_SIZE.SM }, props, { level: props.level + headingLevelOffset, standalone: true, id: slugify(props.children.toString()), sx: Object.assign(Object.assign({}, dynamicTopMarginStyles), customStyles.h4) }))),
                h5: (props) => (_jsx(Heading, Object.assign({ size: HEADING_SIZE.SM }, props, { standalone: true, id: slugify(props.children.toString()), level: props.level + headingLevelOffset, sx: Object.assign(Object.assign({}, dynamicTopMarginStyles), customStyles.h5) }))),
                h6: (props) => (_jsx(Heading, Object.assign({ size: HEADING_SIZE.SM }, props, { standalone: true, id: slugify(props.children.toString()), level: props.level + headingLevelOffset, sx: Object.assign(Object.assign({}, dynamicTopMarginStyles), customStyles.h6) }))),
                code: (props) => _jsx(Caption, Object.assign({}, props)),
                ol: (props) => (_jsx(List, Object.assign({}, props, { ordered: true, sx: Object.assign({ mt: 4 }, customStyles.ol) }))),
                ul: (props) => (_jsx(List, Object.assign({}, props, { sx: Object.assign({ mt: 4 }, customStyles.ul) }))),
                li: (_a) => {
                    var { index, ordered } = _a, props = __rest(_a, ["index", "ordered"]);
                    return (_jsx(ListItem, Object.assign({ index: index }, props, { sx: customStyles.li })));
                },
                p: (props) => (_jsx(Paragraph, Object.assign({}, props, { sx: Object.assign(Object.assign(Object.assign({}, dynamicTopMarginStyles), { fontSize: 'inherit' }), customStyles.p) }))),
                strong: (props) => _jsx(Bold, Object.assign({}, props, { sx: customStyles.strong })),
                em: (props) => _jsx(Italic, Object.assign({}, props, { sx: customStyles.em })),
                img: (props) => {
                    const alt = props.alt || '';
                    const inlineStyles = {};
                    const styles = Object.assign(Object.assign({}, inlineStyles), imageStyles);
                    return _jsx("img", Object.assign({}, props, { style: styles, alt: alt }));
                },
                blockquote: (props) => (_jsx(BlockQuote, Object.assign({}, props, { sx: Object.assign(Object.assign(Object.assign({}, dynamicTopMarginStyles), { my: 5 }), customStyles.blockquote) }))),
            } }, { children: sanitizedContent })) })));
};
